<script setup>
import { computed, ref } from 'vue';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import HugeIcon from 'next/icon/HugeIcon.vue';
import Logo from 'next/icon/Logo.vue';

import { DropdownContainer, DropdownBody } from 'next/dropdown-menu/base';

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');
const searchQuery = ref('');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) => {
    if (a.id === accountId.value) return -1;
    if (b.id === accountId.value) return 1;
    return a.name.localeCompare(b.name);
  });
});

const filteredAccounts = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (!query) return sortedCurrentUserAccounts.value;

  return sortedCurrentUserAccounts.value.filter(account => {
    const roleName = account.custom_role_id
      ? account.custom_role.name
      : account.role;

    return [account.name, roleName].some(value =>
      value?.toLowerCase().includes(query)
    );
  });
});

// eslint-disable-next-line @intlify/vue-i18n/no-missing-keys
const searchWorkspacePlaceholder = computed(() =>
  t('SIDEBAR_ITEMS.SEARCH_WORKSPACE')
);

// eslint-disable-next-line @intlify/vue-i18n/no-missing-keys
const newAccountLabel = computed(() => t('CREATE_ACCOUNT.NEW_ACCOUNT'));

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const accountRoleLabel = account => {
  return account.custom_role_id ? account.custom_role.name : account.role;
};

const accountInitial = account => {
  return account.name?.trim()?.charAt(0)?.toUpperCase() || 'A';
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle, isOpen }">
      <!-- Collapsed view: Logo trigger -->
      <button
        v-if="isCollapsed"
        class="grid flex-shrink-0 place-content-center p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :title="currentAccount.name"
        @click="toggle"
      >
        <Logo class="size-7" />
      </button>
      <!-- Expanded view: Account name trigger -->
      <button
        v-else
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-lg px-2"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="() => showAccountSwitcher && toggle()"
      >
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody
      v-if="showAccountSwitcher || isCollapsed"
      class="z-50 w-[22rem] max-w-[calc(100vw-1rem)]"
    >
      <div
        class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-2 shadow-lg"
      >
        <div class="group relative">
          <HugeIcon
            :icon="Search01Icon"
            :size="18"
            class="pointer-events-none absolute top-1/2 z-[1] -translate-y-1/2 text-n-slate-10 transition-colors group-focus-within:text-n-brand ltr:left-3 rtl:right-3"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="block h-10 w-full rounded-xl border-0 bg-n-surface-1 ps-10 pe-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak transition-all placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand"
            :placeholder="searchWorkspacePlaceholder"
          />
        </div>

        <div class="grid max-h-72 gap-1 overflow-y-auto">
          <button
            v-for="account in filteredAccounts"
            :id="`account-${account.id}`"
            :key="account.id"
            type="button"
            class="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors"
            :class="
              account.id === accountId
                ? 'border-n-strong bg-n-alpha-2 text-n-slate-12'
                : 'border-transparent text-n-slate-11 hover:border-n-weak hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="onChangeAccount(account.id)"
          >
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-lg border text-sm font-semibold"
              :class="
                account.id === accountId
                  ? 'border-n-brand/40 bg-n-brand/10 text-n-brand'
                  : 'border-n-weak bg-n-surface-1 text-n-slate-11'
              "
            >
              {{ accountInitial(account) }}
            </div>

            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium" :title="account.name">
                {{ account.name }}
              </div>
              <div class="truncate text-xs text-n-slate-10 capitalize">
                {{ accountRoleLabel(account) }}
              </div>
            </div>

            <Icon
              v-if="account.id === accountId"
              icon="i-lucide-check"
              class="size-4 shrink-0 text-n-brand"
            />
          </button>
        </div>

        <ButtonNext
          v-if="globalConfig.createNewAccountFromDashboard"
          color="slate"
          variant="faded"
          class="w-full rounded-xl"
          size="sm"
          @click="emitNewAccount"
        >
          {{ newAccountLabel }}
        </ButtonNext>
      </div>
    </DropdownBody>
  </DropdownContainer>
</template>
